import React, { useCallback, useState, useEffect, ReactElement, useMemo, useRef } from 'react';
import css from './CalendarSelect.module.scss';
import { IconCalendar, IconProfile, IconClock } from 'src/content/icons';
import { DateTime } from '@reynolds/datetime';
import CalendarSelectBtn from './CalendarSelectBtn/CalendarSelectBtn';
import CalendarSelectCal from './CalendarSelectCal/CalendarSelectCal';
import { JSXSwitch, JSXCase, JSXIf } from '@framework-controls/jsxconditions';
import CalendarSelectTime from './CalendarSelectTime/CalendarSelectTime';
import { TimeOfDay, Advisor, UpdateApptDateInp, Transportation, rmTimeRec, ShopHour, ApptCust, DepartmentSpec, invalidDates, VehicleRecs, CheckedRecall, ApptOps, Recall, rmTransEligible, ascPageviewInput, ascCtaInteractionInput, ascRetailProcessInput, UpdateApptCustInp, Customer, ascElementConfigurationInput, SpecData } from 'src/models';
import CalendarSelectDisplay from './CalendarSelectDisplay';
import CalendarSelectAdv from './CalendarSelectAdv/CalendarSelectAdv';
import { strings } from 'src/localization';
import { IView } from 'Framework';
import { ComponentMapIds } from '../map';
import { LoadingProgress, Flexbox, ScrollBar } from '@framework-controls/core';
import { TimeDisplayHelper } from 'src/helpers';
import { sentryStartTransaction, sentryStopTransaction } from '@reynolds/error-boundary';
import { convertTo24 } from 'src/helpers/TimeDisplayHelper/TimeDisplayHelper';
import moment from 'moment';
import 'moment/min/locales.min';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import { ascCtaInteraction, ascPageview, ascRetailProcess, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { ViewMode } from 'src/enums';
import classNames from 'classnames';
import { IAppointmentState } from 'src/store/states';
import { getProviderId, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export enum ButtonIds {
    DateSelect = 'dateSel',
    TimeFilter = 'timeFilter',
    AdvSelect = 'advSel',
    ApptSelect = 'apptSelection',
}

export interface IProps {
    timeOfDay: TimeOfDay[];
    advisors: Advisor[];
    allowTimes: boolean;
    allowTimesName: string;
    transportation: Transportation[];
    rmTimes: rmTimeRec[];
    shopHours: ShopHour[];
    specProfile: string;
    apptCust?: ApptCust;
    rmBuiltFlag: boolean;
    specDeptData: DepartmentSpec;
    invalidDates: invalidDates[];
    theme: string;
    apptNum?: string;
    vehicles: VehicleRecs;
    custInfo: Customer;
    pushToMainPanes: (inputView: IView) => void;
    updateApptDate: (inp: UpdateApptDateInp) => void;
    getRMTimes: (specProfile: string, selectedDate: string, selectedAdv: string, recallSelected: boolean) => void;
    clearRMTimes: () => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    updateApptCust: (cust: UpdateApptCustInp) => void;
    checkedRecalls: CheckedRecall[];
    apptOps: ApptOps[];
    vehRecalls: Recall[];
    getInvalidDates: (specProfile: string, selectedDate?: string, selectedAdv?: undefined, recallSelected?: boolean) => void;
    clearInvalidDates: () => void;
    mainPaneViewOrder: IView[];
    locale: string;
    selectedVehicle: string;
    rmTransEligible: rmTransEligible;
    gmossVersion: string;
    apptAdvisor: string;
    apptAdvisorNum: string;
    apptAdvisorUrl: string;
    viewMode?: ViewMode;
    multipleCustomers: boolean;
    appointment: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
}

const CalendarSelect = (props: IProps) => {
    // Get the default time of day to set the start/end times
    let startDate = new DateTime(Date.now()).format('MM/DD/YYYY');
    let startDateValue = new DateTime(Date.now());
    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    for (let i = 0; i < props.invalidDates.length; i++) {
        const constDates = (props.invalidDates[i].date);
        if (constDates === startDate) {
            startDate = new DateTime(Date.now()).addDays(i).format('MM/DD/YYYY');
            startDateValue = new DateTime(Date.now()).addDays(i);
        }
    }
    // #region Local State
    // const [dateSelected, setDateSelected] = useState(new DateTime(Date.now()));
    const [dateSelected, setDateSelected] = useState(startDateValue);
    const [apptTime, setApptTime] = useState('');
    const [apptType, setApptType] = useState<Transportation>({
        type: '',
        title: '',
        titleEN: '',
        titleFR: '',
        information: '',
        informationEN: '',
        informationFR: '',
        description: '',
        descriptionEN: '',
        descriptionFR: '',
        defaultFlag: false,
    });
    const [btnSelected, setBtnSelected] = useState(ButtonIds.DateSelect);
    const [timeOfDay, setTimeOfDay] = useState<TimeOfDay>({
        title: '',
        titleEN: '',
        titleFR: '',
        startTime: '',
        endTime: '',
        default: false,
    });
    const [manualAdvChange, setManualAdvChange] = useState<boolean>(false);
    const [advSelected, setAdvSelected] = useState(props.apptAdvisorNum ?? '');
    const [advSelectedName, setAdvSelectedName] = useState(props.apptAdvisor ?? '');
    const [advSelectedUrl, setAdvSelectedUrl] = useState(props.apptAdvisorUrl ?? '');
    const [recallSelected, setRecallSelected] = useState<boolean>(false);
    const [calendarDate, setCalendarDate] = useState(new DateTime(Date.now()).format('MM/DD/YYYY'));
    const [loadingGetInvalidDates, setLoadingGetInvalidDates] = useState(false);
    const onLoad = useRef(true);
    const pageViewed = useRef(false);
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;
    // #endregion

    // #region Functions
    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.ServiceTab ||
            (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CalendarSelect)) {
            if (onLoad.current === true) {
                props.clearInvalidDates();
                props.getInvalidDates(props.specProfile, startDate, undefined, recallSelected);
                setBtnSelected(ButtonIds.DateSelect);
                if (props.apptCust?.custFirstName === '' && props.custInfo?.firstName !== '' && !props.multipleCustomers) {
                    const apptCustInp: UpdateApptCustInp = {
                        nameId: props.custInfo?.nameId,
                        firstName: props.custInfo?.firstName,
                        lastName: props.custInfo?.lastName,
                        emailAddress: props.custInfo?.emailAddress,
                        phoneNumber: props.custInfo?.phoneNumber,
                        phoneType: props.custInfo?.phoneType,
                        prefContact: props.custInfo?.prefContact,
                        loggedIn: props.custInfo?.login,
                    };
                    props.updateApptCust(apptCustInp);
                }
                onLoad.current = false;
            }
        }
    }, [props.clearInvalidDates, props.getInvalidDates, startDate, calendarDate, recallSelected,
        props.specProfile, props.mainPaneViewOrder, props.custInfo, props.updateApptCust, props.apptCust?.custFirstName,
        props.multipleCustomers]);

    useEffect(() => {
        if (props.rmBuiltFlag &&
            props.invalidDates.length === 0 &&
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.QuickTime &&
            !loadingGetInvalidDates
        ) {
            props.clearInvalidDates();
            props.getInvalidDates(props.specProfile, startDate, undefined, recallSelected);
            setLoadingGetInvalidDates(true);
        }
    }, [props.rmBuiltFlag, props.clearInvalidDates, props.getInvalidDates,
        props.mainPaneViewOrder, props.specProfile, startDate, recallSelected,
        props.invalidDates.length, loadingGetInvalidDates]);

    /* Set the initial Advisor option based on the chosen Specs option - defAdvOption
       1) Default
       2) Previous, then Default
       3) Previous
    */
    useEffect(() => {
        if ((!manualAdvChange && !props.apptAdvisor &&
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CalendarSelect)) {
            let name = '';
            let advNum = '';
            let advUrl = '';
            const defAdvOption = props.specDeptData.defAdvOption;
            const defaultAdv = props.advisors.find(adv => adv.defaultFlag);
            const previousAdvNum = props.vehicles[props.selectedVehicle]?.lastAdv;
            const previousAdv = props.advisors.find(adv => adv.advisorNum === previousAdvNum);
            let selectedAdv: Advisor = {
                advisorNum: '',
                advisorName: '',
                advisorPhone: '',
                advisorImage: '',
                defaultFlag: false,
            };
            switch (defAdvOption) {
                case '0':
                case '1':
                case '4':
                    selectedAdv = defaultAdv!;
                    break;
                case '2':
                    selectedAdv = defaultAdv!;
                    if (previousAdv) {
                        selectedAdv = previousAdv!;
                    }
                    break;
                case '3':
                    selectedAdv = previousAdv!;
                    if (!previousAdv) {
                        selectedAdv = defaultAdv!;
                    }
                    break;
                default:
                    selectedAdv = defaultAdv!;
                    break;
            }
            // Adjust return data if default advisor is the Any Advisor option
            if (props.specDeptData.anyAdvDefault && selectedAdv === defaultAdv) {
                name = props.specDeptData.advisorOption || '';
                advNum = '';
                advUrl = '';
            } else {
                name = selectedAdv?.advisorName || '';
                advNum = selectedAdv?.advisorNum || '';
                advUrl = selectedAdv?.advisorImage || '';
            }
            setAdvSelectedName(name);
            setAdvSelected(advNum);
            setAdvSelectedUrl(advUrl);
            setApptTime('');
        }
    }, [
        manualAdvChange,
        props.advisors,
        props.specDeptData.Advisors,
        props.vehicles,
        props.specDeptData.defAdvOption,
        props.specDeptData.anyAdvDefault,
        props.specDeptData.advisorOption,
        props.specDeptData.defAdvisorNum,
        props.apptAdvisor,
        props.mainPaneViewOrder,
        props.selectedVehicle,
    ]);

    // Swap Any Advisor literal between English and French when selected
    useEffect(() => {
        if ((advSelectedName === props.specDeptData.advisorOptionEN ||
            advSelectedName === props.specDeptData.advisorOptionFR) && pageViewed.current &&
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CalendarSelect) {
            let anyAdvName = props.specDeptData.advisorOptionEN;
            if (props.locale === 'fr-CA') {
                anyAdvName = props.specDeptData.advisorOptionFR;
            }
            setAdvSelectedName(anyAdvName);
            setAdvSelected('');
            setAdvSelectedUrl('');
            setApptTime('');
        }
    }, [
        advSelectedName,
        props.specDeptData.advisorOptionEN,
        props.specDeptData.advisorOptionFR,
        props.locale,
        props.mainPaneViewOrder,
    ]);

    useEffect(() => {
        // Need to determine whether any of the operations in ApptOps are recalls
        const selectedRecalls =
            props.apptOps.filter((apptOp) => apptOp.concern.indexOf(strings.RecallOp) !== -1);
        // If there are checked recalls on an appointment being created, or
        // if there are recalls on an appointment that has already been created assign true to
        // recallSelected.
        if (props.checkedRecalls.length > 0 || selectedRecalls.length > 0) {
            setRecallSelected(true);
        } else {
            setRecallSelected(false);
        }
    }, [props.checkedRecalls, setRecallSelected, recallSelected, props.apptOps, props.vehRecalls]);

    const onDateChange = useCallback((date: DateTime) => {
        sentryStartTransaction('RMTimes', 'Date Change');
        setDateSelected(date);
        setBtnSelected(ButtonIds.ApptSelect);
        setApptTime('');
        props.clearRMTimes();
        props.getRMTimes(props.specProfile, date.toDateString(), advSelected, recallSelected);
    }, [props.specProfile, props.getRMTimes, advSelected, props.clearRMTimes, recallSelected]);

    // Any time the user navigates to a new month in the calendar, we need to grab a new range of invalid dates
    const onMonthChange = useCallback((date: DateTime) => {
        props.clearInvalidDates();
        props.getInvalidDates(props.specProfile, date.toShortDateString(), undefined, recallSelected);
        setCalendarDate(date.toDateString());
    }, [props.clearInvalidDates, props.getInvalidDates, props.specProfile, recallSelected]);

    const onTimeChange = useCallback((time: TimeOfDay) => {
        if (!props.rmBuiltFlag) {
            sentryStartTransaction('RMTimes', 'Time Change');
            props.clearRMTimes();
            props.getRMTimes(props.specProfile, dateSelected.toDateString(), advSelected, recallSelected);
        }
        setTimeOfDay(time);
        setBtnSelected(ButtonIds.ApptSelect);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Update,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, '', props.vehicles, props.selectedVehicle);
    }, [advSelected, props.clearRMTimes, props.getRMTimes, props.specProfile,
        dateSelected, props.rmBuiltFlag, recallSelected, props.vehicles, props.selectedVehicle]);

    const onApptTimeChange = useCallback((time: string, transportation: Transportation) => {
        setApptTime(time);
        setApptType(transportation);
    }, []);

    const onAdvChange = useCallback((advisor: Advisor, event: any) => {
        setAdvSelected(advisor.advisorNum);
        setAdvSelectedName(advisor.advisorName);
        setAdvSelectedUrl(advisor.advisorImage);
        setBtnSelected(ButtonIds.ApptSelect);
        setManualAdvChange(true);
        setApptTime('');

        sentryStartTransaction('RMTimes', 'Advisor Change');
        props.clearRMTimes();
        props.getRMTimes(props.specProfile, dateSelected.toDateString(), advisor.advisorNum, recallSelected);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: advisor.advisorName,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicles, props.selectedVehicle);
    }, [props.specProfile, dateSelected, props.getRMTimes, props.clearRMTimes,
        recallSelected, props.vehicles, props.selectedVehicle]);

    const apptSelectionHandler = (e: any) => {
        // Push the selected date, time, type and advisor to the Appointment State.
        const inp: UpdateApptDateInp = {
            selDate: dateSelected.toShortDateString(),
            selTime: apptTime,
            selAdv: advSelected,
            selTrans: apptType.title,
            selAdvName: advSelectedName,
            selAdvImage: advSelectedUrl,
            selTransFlag: apptType.type,
            selApptNum: props.apptNum,
        };
        props.updateApptDate(inp);
        const vehicle = props.vehicles.find(v => v.id === props.selectedVehicle);
        if ((props.apptCust?.custNameId && props.apptCust?.custNameId !== '' &&
            vehicle && (vehicle.isOEM !== 'Y' || (vehicle.isOEM === 'Y' && props.vehicles.some(v => v.isOEM !== 'Y'))) &&
            (apptType.type !== 'DE' && apptType.type !== 'RN')) ||
            ((props.apptCust?.custFirstName && props.apptCust?.custLastName && props.apptCust?.custEmailAddress &&
            props.apptCust?.custPhoneNumber) &&
            (apptType.type !== 'DE' && apptType.type !== 'RN'))) {
            if ((apptType.type === 'P' && props.gmossVersion === '1.4.2') || apptType.type === 'U') {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.PickupDelivery,
                    component: ComponentMapIds.PickupDelivery,
                });
            } else {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.Summary,
                    component: ComponentMapIds.Summary,
                });
            }
        } else {
            props.pushToMainPanes({
                viewId: ComponentMapIds.NewCustInfo,
                component: ComponentMapIds.NewCustInfo,
            });
        }
        setManualAdvChange(false);
        pageViewed.current = false;
        onLoad.current = true;
        const ascRetailProcessInp: ascRetailProcessInput = {
            pageType: 'service',
            flowStage: '5b',
            flowOutcome: 'proceed',
            flowName: 'appointment',
            elementTitle: 'Select Appointment',
        };
        ascRetailProcess(ascRetailProcessInp, props.vehicles, props.selectedVehicle);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        if (isSDAnalyticsEnabled && apptType) {
            sendSdEvent('scheduleServiceTransportFinish', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                scheduleServiceTransportType: apptType.title,
            });
        }
    };

    // #endregion Functions

    // Filter display times to account for shopHours specs
    // const rmTimes = filteredTimes.filter((x) => x.transFlag === trans.type);
    const convertedTimes = props.rmTimes.map((time) => {
        const convTime = TimeDisplayHelper.convertTo24(time.displayTime);

        return convTime;
    });

    let shopHoursIndex = 0;
    if (props.shopHours) {
        switch (dateSelected.getDayOfWeek()) {
            case 'Monday':
                shopHoursIndex = 0;
                break;
            case 'Tuesday':
                shopHoursIndex = 1;
                break;
            case 'Wednesday':
                shopHoursIndex = 2;
                break;
            case 'Thursday':
                shopHoursIndex = 3;
                break;
            case 'Friday':
                shopHoursIndex = 4;
                break;
            case 'Saturday':
                shopHoursIndex = 5;
                break;
            case 'Sunday':
                shopHoursIndex = 6;
                break;
            default:
                shopHoursIndex = 0;
        }
    }

    const timeOfDayFilter: number[] = [];
    if (props.shopHours) {
        convertedTimes.forEach((timeSlot) => {
            if ((timeSlot >= parseInt(timeOfDay.startTime) && timeSlot <= parseInt(timeOfDay.endTime)) || timeSlot) {
                timeOfDayFilter.push(timeSlot);
            }
        });
    }

    const filteredTimeSlots: string[] = [];
    if (props.shopHours && props.shopHours[shopHoursIndex] && props.shopHours[shopHoursIndex].open1) {
        timeOfDayFilter.forEach((timeSlot) => {
            if (timeSlot >= parseInt(props.shopHours![shopHoursIndex].open1) &&
                timeSlot < parseInt(props.shopHours![shopHoursIndex].close1)) {
                if (TimeDisplayHelper.convertTimeZeros('' + timeSlot).length < 7) {
                    filteredTimeSlots.push('0' + TimeDisplayHelper.convertTimeZeros('' + timeSlot).toLowerCase());
                } else {
                    filteredTimeSlots.push(TimeDisplayHelper.convertTimeZeros(timeSlot + '').toLowerCase());
                }
            } else if (timeSlot >= parseInt(props.shopHours![shopHoursIndex].open2) &&
                timeSlot < parseInt(props.shopHours![shopHoursIndex].close2)) {
                if (TimeDisplayHelper.convertTimeZeros('' + timeSlot).length < 7) {
                    filteredTimeSlots.push('0' + TimeDisplayHelper.convertTimeZeros('' + timeSlot).toLowerCase());
                } else {
                    filteredTimeSlots.push(TimeDisplayHelper.convertTimeZeros(timeSlot + '').toLowerCase());
                }
            } else if (timeSlot >= parseInt(props.shopHours![shopHoursIndex].open3) &&
                timeSlot < parseInt(props.shopHours![shopHoursIndex].close3)) {
                if (TimeDisplayHelper.convertTimeZeros('' + timeSlot).length < 7) {
                    filteredTimeSlots.push('0' + TimeDisplayHelper.convertTimeZeros('' + timeSlot).toLowerCase());
                } else {
                    filteredTimeSlots.push(TimeDisplayHelper.convertTimeZeros(timeSlot + '').toLowerCase());
                }
            }
        });
    }

    // filter on props.apptTimes for matching time slots after converting them to match displayTime
    let finalFilter = props.rmTimes;
    if (props.shopHours) {
        finalFilter = props.rmTimes.filter((x) => filteredTimeSlots.includes(x.displayTime.toLowerCase()));
    }
    if (props.shopHours[shopHoursIndex].open1 === '' &&
        props.shopHours[shopHoursIndex].open2 === '' &&
        props.shopHours[shopHoursIndex].open3 === '') {
        finalFilter = props.rmTimes;
    }

    // determine the unavailable transportation types
    let waiterAvailable = true;
    let loanerAvailable = true;
    let rentalAvailable = true;
    let conciergeAvailable = true;
    let cadillacAvailable = true;
    let PPUDWLAvailable = true;
    props.apptOps.forEach((apptOp) => {
        if (apptOp.opWaiter === 'N') {
            waiterAvailable = false;
        }
        if (apptOp.opLoaner === 'N') {
            loanerAvailable = false;
        }
        if (apptOp.opRental === 'N') {
            rentalAvailable = false;
        }
        if (apptOp.opConcierge === 'N') {
            conciergeAvailable = false;
        }
        if (apptOp.opCadillac === 'N') {
            cadillacAvailable = false;
        }
        if (apptOp.opPPUDWL === 'N') {
            PPUDWLAvailable = false;
        }
    });
    let transAvailable = false;
    props.transportation.forEach((transportation) => {
        if ((transportation.type === 'W') && waiterAvailable) {
            transAvailable = true;
        }
        if ((transportation.type === 'L') && loanerAvailable) {
            transAvailable = true;
        }
        if ((transportation.type === 'R') && rentalAvailable) {
            transAvailable = true;
        }
        if ((transportation.type === 'P') && conciergeAvailable) {
            transAvailable = true;
        }
        if ((transportation.type === 'C') && cadillacAvailable) {
            transAvailable = true;
        }
        if ((transportation.type === 'U') && PPUDWLAvailable) {
            transAvailable = true;
        }
        if ((transportation.type !== 'W') && (transportation.type !== 'L') && (transportation.type !== 'R') &&
            (transportation.type !== 'P') && (transportation.type !== 'C') && (transportation.type !== 'U')) {
            transAvailable = true;
        }
    });

    // filter the advisor times that will be displayed by adjusting the custom time range slider.
    const filteredTimesDisplayed = useMemo(() => {
        const tempStart = parseInt(timeOfDay.startTime, 10);
        const tempEnd = parseInt(timeOfDay.endTime, 10);

        return (
            props.rmTimes.filter((x) => (convertTo24(x.displayTime) >= tempStart &&
                convertTo24(x.displayTime) <= tempEnd) || x.transFlag === 'N')
        );
    }, [props.rmTimes, timeOfDay]);

    let timesDisplayed = true;
    if (filteredTimesDisplayed.length === 0) {
        timesDisplayed = false;
    }

    // determine if the custom time range is set to shop hours closed times.
    // return unavailable message if it is.
    const customRangeStart = parseInt(timeOfDay.startTime);
    const customRangeEnd = parseInt(timeOfDay.endTime);
    const shopClose1Int = parseInt(props.shopHours[shopHoursIndex].close1);
    const shopOpen2Int = parseInt(props.shopHours[shopHoursIndex].open2);
    const shopClose2Int = parseInt(props.shopHours[shopHoursIndex].close2);
    const shopOpen3Int = parseInt(props.shopHours[shopHoursIndex].open3);
    let shopOpen1 = true;
    if (customRangeStart >= shopClose1Int && customRangeEnd <= shopOpen2Int) {
        shopOpen1 = false;
    }
    let shopOpen2 = true;
    if (customRangeStart >= shopClose2Int && customRangeEnd <= shopOpen3Int) {
        shopOpen2 = false;
    }
    // loop to determine if there is a time slot available on the custom time range beginning or end.
    for (let i = 0; i < finalFilter.length; i++) {
        const individualTimes = TimeDisplayHelper.convertTo24(finalFilter[i].displayTime);
        if (individualTimes === customRangeStart) {
            shopOpen1 = true;
            break;
        }
        if (individualTimes === customRangeEnd) {
            shopOpen1 = true;
            break;
        }
    }

    // determine which no times availble message to display if that is the case
    let noTimeMessage = strings.NoTimesInTimeRange;
    if (props.rmTimes.length === 0) {
        noTimeMessage = strings.NoTimes;
    } else if (!transAvailable) {
        noTimeMessage = strings.NoTimesForServices;
    }
    const processingDate = dateSelected.toDate();
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const dateLabel = processingUTCDate.format('dddd, MMMM D, YYYY');

    // make sure time label matches the date's shop hours
    let dayIndex = props.timeOfDay.findIndex((x) => x.title === timeOfDay.title);
    if (dayIndex===-1) {
        dayIndex = props.timeOfDay.findIndex((x) => x.default);
    }
    let defaultStart = 0;
    let defaultEnd = 0;
    if (dayIndex !== -1) {
        defaultStart = parseInt(props.timeOfDay[dayIndex].startTime, 10);
        defaultEnd = parseInt(props.timeOfDay[dayIndex].endTime, 10);
    }
    if (timeOfDay.title === strings.CustomRange) {
        defaultStart = parseInt(timeOfDay.startTime, 10);
        defaultEnd = parseInt(timeOfDay.endTime, 10);
    }
    let timeRangeMin = props.shopHours !== undefined ? parseInt(props.shopHours[shopHoursIndex].open1, 10) : defaultStart;
    let timeRangeMax = props.shopHours !== undefined ? parseInt(props.shopHours[shopHoursIndex].close1, 10) : defaultEnd;
    if (props.shopHours &&
        props.shopHours[shopHoursIndex].open1 === '' &&
        props.shopHours[shopHoursIndex].open2 === '' &&
        props.shopHours[shopHoursIndex].open3 === '') {
        timeRangeMin = defaultStart;
    }
    if (props.shopHours &&
        props.shopHours[shopHoursIndex].close1 === '' &&
        props.shopHours[shopHoursIndex].close2 === '' &&
        props.shopHours[shopHoursIndex].close3 === '') {
        timeRangeMax = defaultEnd;
    }
    if (props.shopHours[shopHoursIndex].close1) {
        timeRangeMax = parseInt(props.shopHours[shopHoursIndex].close1);
    }
    if (props.shopHours[shopHoursIndex].close2) {
        timeRangeMax = parseInt(props.shopHours[shopHoursIndex].close2);
    }
    if (props.shopHours[shopHoursIndex].close3) {
        timeRangeMax = parseInt(props.shopHours[shopHoursIndex].close3);
    }
    const timeRangeLabelTime: TimeOfDay = {
        title: timeOfDay.title,
        titleEN: timeOfDay.titleEN,
        titleFR: timeOfDay.titleFR,
        startTime: timeRangeMin > defaultStart ? timeRangeMin+'' : defaultStart+'',
        endTime: timeRangeMax < defaultEnd ? timeRangeMax+'' : defaultEnd + '',
        default: timeOfDay.default,
    };
    if (parseInt(timeRangeLabelTime.startTime) > timeRangeMax) {
        timeRangeLabelTime.startTime = timeRangeMax+'';
    }
    if (parseInt(timeRangeLabelTime.endTime) < timeRangeMin) {
        timeRangeLabelTime.endTime = timeRangeMin+'';
    }
    useEffect(() => {
        let defaultTime = props.timeOfDay.find((x) => x.default &&
            parseInt(x.startTime) < timeRangeMax && parseInt(x.endTime) > timeRangeMin);

        if (defaultTime === undefined) {
            defaultTime = props.timeOfDay.find((x) => {
                // Set the first interval within the range as the default.
                if (parseInt(x.startTime) < timeRangeMax && parseInt(x.endTime) > timeRangeMin) {
                    return x;
                } else {
                // if all time of day intervals are out of range, return the default.
                    return x.default;
                }
            });
        }

        if (defaultTime) {
            setTimeOfDay(defaultTime);
        }
    }, [props.timeOfDay, timeRangeMax, timeRangeMin]);

    let timeRangeLabel = timeOfDay.title + ' | ' + TimeRangeDisplay(timeRangeLabelTime);
    if (defaultStart >= timeRangeMax || defaultEnd <= timeRangeMin) {
        timeRangeLabel = timeOfDay.title;
    }
    // if no times message appears and btnSelected is apptSelection, we need to send the ascPageview trigger here.
    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CalendarSelect &&
            btnSelected === ButtonIds.ApptSelect && props.rmBuiltFlag &&
            (finalFilter.length === 0 || !transAvailable || !timesDisplayed || !shopOpen1 || !shopOpen2)) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder,
        finalFilter.length,
        transAvailable,
        timesDisplayed,
        shopOpen1,
        shopOpen2,
        btnSelected,
        props.rmBuiltFlag,
        props.vehicles,
        props.selectedVehicle]);

    // Get the shop hours to pass into the time component
    const hours = props.shopHours.find((x) => x.day === dateSelected.getDayOfWeek());
    let calTimeArea: ReactElement = <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} />;
    if (props.rmBuiltFlag) {
        sentryStopTransaction();
        calTimeArea = (finalFilter.length > 0) && transAvailable && timesDisplayed && shopOpen1 && shopOpen2 ?
            < CalendarSelectDisplay
                id='calDisplay'
                dateSelected={dateSelected}
                timeOfDay={timeOfDay}
                transportation={props.transportation}
                rmTimes={finalFilter}
                advSelectedName={advSelectedName}
                apptTimeChange={onApptTimeChange}
                apptHandler={apptSelectionHandler}
                theme={props.theme}
                apptOps={props.apptOps}
                locale={props.locale}
                rmTransEligible={props.rmTransEligible}
                mainPaneViewOrder={props.mainPaneViewOrder}
                btnSelected={btnSelected}
                vehicles={props.vehicles}
                selectedVehicle={props.selectedVehicle}
                gmossVersion={props.gmossVersion}
                appointment={props.appointment}
                specData={props.specData}
                sessionId = {props.sessionId}
                lexusMCLaunched = {props.lexusMCLaunched}
                lexusTDMCLaunched = {props.lexusTDMCLaunched}
                toyotaMCLaunched = {props.toyotaMCLaunched}
                toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
            /> :
            <h2 className={css.noTimes}> {noTimeMessage}</h2>;
    }

    let allowAdvChange = false;
    if (props.specDeptData.allwAdvChng === 'Y') {
        allowAdvChange = true;
    }

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Calendar Select', '');
    }, [
        props.addLogEntry,
    ]);

    // If the user gets here from the summary screen, it's possible that the date/time/advisor selected
    // is no longer available. So, default to the Date Select tab in order to force RMTimes to be
    // retrieved again.
    useEffect(() => {
        if (props.mainPaneViewOrder.length > 1) {
            if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 2].viewId === 'Summary') {
                setBtnSelected(ButtonIds.DateSelect);
            }
        }
    }, [props.mainPaneViewOrder]);

    const buttonHandler = useCallback((id: ButtonIds, e: any) => {
        if (id === 'dateSel' && btnSelected !== 'dateSel') {
            props.clearInvalidDates();
            props.getInvalidDates(props.specProfile, dateSelected.toShortDateString(), undefined, recallSelected);
        }
        setBtnSelected(id);
        let elementText = '';
        if (id === ButtonIds.DateSelect) {
            elementText = dateLabel;
        } else if (id === ButtonIds.TimeFilter) {
            elementText = timeRangeLabel;
        } else if (id === ButtonIds.AdvSelect) {
            elementText = advSelectedName;
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: elementText,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_tab',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: elementText,
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [recallSelected, props.clearInvalidDates, props.getInvalidDates, props.specProfile,
        btnSelected, dateSelected, advSelectedName, dateLabel, timeRangeLabel, props.vehicles, props.selectedVehicle]);

    const fill = (props.theme === 'hyundai' && btnSelected === ButtonIds.TimeFilter) ? 'white' : 'inherit';
    let apptTimeLabel = apptTime && <strong>{`${strings.Selected} ${dateLabel} ${strings.At} ${apptTime}`}</strong>;

    if (props.theme === 'chevrolet' || props.theme === 'honda' || props.theme === 'buick' || props.theme === 'bmw') {
        apptTimeLabel = apptTime && (
            <span>
                <strong>{strings.Selected}</strong>
                {dateLabel} {strings.At} {apptTime}
            </span>
        );
    }

    const hrClassMap = {
        honda: css.hrHonda,
        bmw: css.hrBMW,
    };
    const hrClass = hrClassMap[props.theme] || css.hr;

    const selectApptStyles = classNames(
        css.selectAppt,
        {
            [css.selectApptFord]: props.theme === 'ford',
        },
    );

    return (
        <React.Fragment>
            <ScrollBar scrollVertical>
                <div className={css.calendarSelect}>
                    <div className={css.calendarMenu}>
                        <Flexbox
                            container
                            spacing={0}
                            direction={'row'}
                            noMargin={false}
                            alignItems={'center'}
                            justify={'space-around'}
                        >
                            <CalendarSelectBtn
                                id={ButtonIds.DateSelect}
                                label={dateLabel}
                                icon={<IconCalendar style={{ height: '1.5rem', margin: '5px' }} />}
                                onClick={buttonHandler}
                                selected={btnSelected === ButtonIds.DateSelect}
                                theme={props.theme}
                                advisorURL={advSelectedUrl}
                                advisorName = {advSelectedName}
                            />
                            <CalendarSelectBtn
                                id={ButtonIds.TimeFilter}
                                label={timeRangeLabel}
                                icon={<IconClock style={{ height: '2rem', fill: fill}} />}
                                onClick={buttonHandler}
                                selected={btnSelected === ButtonIds.TimeFilter}
                                theme={props.theme}
                                advisorURL={advSelectedUrl}
                                advisorName = {advSelectedName}
                            />
                            <JSXIf Condition={allowAdvChange}>
                                <CalendarSelectBtn
                                    id={ButtonIds.AdvSelect}
                                    label={advSelectedName}
                                    icon={<IconProfile style={{ height: '1.5rem', margin: '5px' }} />}
                                    onClick={buttonHandler}
                                    selected={btnSelected === ButtonIds.AdvSelect}
                                    theme={props.theme}
                                    advisorURL={advSelectedUrl}
                                    advisorName = {advSelectedName}
                                />
                            </JSXIf>
                        </Flexbox>
                    </div>
                    <JSXSwitch>
                        <JSXCase Condition={btnSelected === ButtonIds.DateSelect}>
                            <div className={hrClass}>
                                {strings.ApptDay}
                            </div>
                            <CalendarSelectCal
                                id='calSel'
                                onDateChange={onDateChange}
                                dateSelected={dateSelected}
                                invalidDates={props.invalidDates}
                                onMonthChange={onMonthChange}
                                locale={props.locale}
                                clearInvalid = {props.clearInvalidDates}
                                getInvalid = {props.getInvalidDates}
                                specProfile = {props.specProfile}
                                mainPaneViewOrder={props.mainPaneViewOrder}
                                vehicles={props.vehicles}
                                selectedVehicle={props.selectedVehicle}
                                pageViewed={pageViewed}
                                theme={props.theme}
                            />
                        </JSXCase>
                        <JSXCase Condition={btnSelected === ButtonIds.TimeFilter}>
                            <div className={hrClass}>
                                {strings.TimeFilter}
                            </div>
                            <CalendarSelectTime
                                id='calTime'
                                timeSelect={onTimeChange}
                                timeOfDay={props.timeOfDay}
                                selectedTOD={timeOfDay.title===strings.CustomRange ?
                                    timeOfDay: timeRangeLabelTime}
                                shopHours={hours}
                                theme={props.theme}
                                mainPaneViewOrder={props.mainPaneViewOrder}
                                vehicles={props.vehicles}
                                selectedVehicle={props.selectedVehicle}
                            />
                        </JSXCase>
                        <JSXCase Condition={btnSelected === ButtonIds.AdvSelect}>
                            <div className={css.hr}>
                                {strings.AdvisorSelect}
                            </div>
                            <CalendarSelectAdv
                                id='calAdv'
                                advisors={props.advisors}
                                advChange={onAdvChange}
                                selectAdv={advSelected}
                                allowTimes={props.allowTimes}
                                allowTimesName={props.allowTimesName}
                                deptSpecs={props.specDeptData}
                                theme={props.theme}
                                mainPaneViewOrder={props.mainPaneViewOrder}
                                vehicles={props.vehicles}
                                selectedVehicle={props.selectedVehicle}
                            />
                        </JSXCase>
                        <JSXCase Default>
                            {calTimeArea}
                        </JSXCase>
                    </JSXSwitch>
                </div>
            </ScrollBar>

            <div className={css.selectedAppt} id='selectedAppt'>
                <hr className={css.footerHr}></hr>
                <div className={selectApptStyles}>
                    {apptTimeLabel}
                    <SVPButton
                        id='proceedBtn'
                        variant={'primaryBtnAlt'}
                        message={strings.Proceed}
                        handler={apptSelectionHandler}
                        tooltip={strings.ProceedTip}
                        isDisabled={apptTime ? false : true}
                        theme={props.theme}
                        noArrow={noArrow}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

function TimeRangeDisplay(time: TimeOfDay) {
    let start = time.startTime;
    let end = time.endTime;
    if (time.startTime === '' || isNaN(parseInt(time.startTime))) {
        start = '0000';
    }
    if (time.endTime === '' || isNaN(parseInt(time.endTime))) {
        end = '0000';
    }
    const startTime = TimeDisplayHelper.convertTime(start);
    const endTime = TimeDisplayHelper.convertTime(end);
    const range = startTime + ' - ' + endTime;

    return range;
}

export default CalendarSelect;